import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Menu from "Components/Menu/Menu";
import { capitalizeFirstLetter } from "Utils/Utils";
import useVipAccountCache from "Utils/Hooks/useVipAccountCached";
import useSession from "Utils/Hooks/useSession";
import useMode, { MODE } from "Utils/Hooks/useMode";
import { CUSTOMER_SERVICE_PHONE_NUMBER, PRODUCTTYPES } from "Utils/Constants";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import vippreferredlogo from "../../Assets/Img/vip-preferred-ach-logo-2.svg";
import paybybanklogo from "Assets/Img/PBB_logo_horizontal.svg";
import "./Header.scss";
import { faro } from "@grafana/faro-react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import accountIcon from "Assets/Icons/account_circle.svg";
import helpIcon from "Assets/Icons/help_outline.svg";
import Popover from "./Popover";
import useView, { VIEW } from "Utils/Hooks/useView";

const Header = ({ isVipEnrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [accountTooltipOpen, setAccountTooltipOpen] = useState(false);
  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
  const mode = useMode();
  const manageFundingSources = mode === MODE.manageBanks;
  const view = useView(); // Cache view selection
  const { session, invalidateSession } = useSession();
  const isVIPPreferred = session?.patronProductType === PRODUCTTYPES.PREFERRED;
  const productType = isVIPPreferred ? "VIP Preferred" : "VIP Online";
  const productRoutingEnabled = session?.toggles?.productRoutingEnabled;
  const logoSrc = productRoutingEnabled ? paybybanklogo : vippreferredlogo;
  const logoAlt = productRoutingEnabled ? "Pay by Bank" : "VIP Preferred";
  const { vipAccount, setVipAccount } = useVipAccountCache();
  const vipApi = useVipApiFetcher();
  let history = useHistory();
  const userName = `${capitalizeFirstLetter(vipAccount?.firstName)} ${capitalizeFirstLetter(vipAccount?.lastName)}`;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCashierPage = view === VIEW.cashier;
  const logout = async () => {
    handleAccountTooltipClose();
    await NotifyLogout();
  };
  const dashboardClick = () => {
    if (userName && !manageFundingSources) {
      history.push("/dashboard");
      faro.api.pushEvent("User clicked on Dashboard");
    }
  };

  const logo = (
    <div onClick={dashboardClick}>
      <img className="vip-preferred" src={logoSrc} alt={logoAlt} />
    </div>
  );

  if (!mode && session) {
    history.push("/invalid");
  }

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleAccountTooltip = () => {
    setAccountTooltipOpen((curr) => !curr);
    if (helpTooltipOpen) {
      setHelpTooltipOpen(false);
    }
  };

  const handleAccountTooltipClose = () => {
    setAccountTooltipOpen(false);
  };

  const toggleHelpTooltip = () => {
    setHelpTooltipOpen((curr) => !curr);
    if (accountTooltipOpen) {
      setAccountTooltipOpen(false);
    }
  };

  const handleHelpTooltipClose = () => {
    setHelpTooltipOpen(false);
  };

  async function NotifyLogout() {
    const sessionId = JSON.parse(
      global.sessionStorage.getItem("vip-patron-session"),
    ).sessionId;
    if (!session.transactionSucceeded) {
      vipApi
        .fetch("/api/usertracking/cancelflow", "POST", {
          transactionStage:
            mode === "withdraw"
              ? "Withdrawal"
              : mode === MODE.manageBanks
                ? "AddFundingSource"
                : "Deposit",
          sessionId: sessionId,
        })
        .then((response) => {
          if (response.ok) {
            faro.api.pushEvent("User logged out");
            setVipAccount(null);
            invalidateSession();
            history.push("/logout");
          } else {
            faro.api.pushError(new Error("Logout Request Error"), {
              context: response,
            });
            if (response.status === 500) {
              console.error(response.statusText);
              history.push({
                pathname: "/error",
              });
            } else if (response.status === 401) {
              console.error(response.statusText);
              history.push({
                pathname: "/expired",
              });
            } else {
              response.json().then((data) => {
                console.error(data);
                onError(data);
              });
            }
          }
        })
        .catch((error) => {
          console.error(error);
          faro.api.pushError(new Error("Logout Network Error"), {
            context: error,
          });
          history.push({
            pathname: "/error",
          });
        });
    } else {
      setVipAccount(null);
      invalidateSession();
      history.push("/logout");
    }
  }

  function onError(response) {
    history.push({
      pathname: "/error",
      state: {
        isError: true,
        showButton: true,
        title: "Request Failed. Please Try Again",
        message:
          response.patronErrorMessage ??
          "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-30 so that we may better serve you.",
        allowRetry: false,
      },
    });
  }

  return (
    logo && (
      <Box className={"header-container"}>
        <Box
          sx={{
            padding: isCashierPage ? "12px 16px" : "24px 16px",
            pointerEvents: isCashierPage ? "none" : "auto",
            justifyContent: "center",
            display: "flex",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: { md: "var(--spacing-lg)" },
              maxWidth: "1440px",
              height: "49px",
              width: "100%",
            }}
          >
            {!isCashierPage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "1",
                }}
              ></Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "1",
              }}
            >
              <Box onClick={dashboardClick}>
                <img className="vip-preferred" src={logoSrc} alt={logoAlt} />
              </Box>
            </Box>
            {!isCashierPage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flex: "1",
                }}
              >
                {isMobile ? (
                  <IconButton
                    data-testid="mobile-menu-btn"
                    onClick={toggleMenu}
                    aria-label={
                      isOpen ? "Close navigation menu" : "Open navigation menu"
                    }
                    aria-controls={isOpen ? "heading-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={isOpen ? "true" : undefined}
                    sx={{ color: "white" }}
                  >
                    {isOpen ? <Close /> : <MenuIcon />}
                  </IconButton>
                ) : (
                  <ClickAwayListener
                    onClickAway={() => {
                      handleHelpTooltipClose();
                      handleAccountTooltipClose();
                    }}
                  >
                    <Box>
                      <Popover
                        tooltipOpen={accountTooltipOpen}
                        onClose={handleAccountTooltipClose}
                        content={
                          <div>
                            <div>Welcome, {userName}!</div>
                            <Link
                              sx={{
                                color: "#08467F",
                                fontWeight: 700,
                                "&:hover": {
                                  cursor: "pointer",
                                },
                              }}
                              onClick={logout}
                              data-testid="logout-btn"
                            >
                              Close {productType}
                            </Link>
                          </div>
                        }
                      >
                        {vipAccount?.firstName && (
                          <IconButton
                            onClick={toggleAccountTooltip}
                            data-testid="profile-btn"
                          >
                            <img src={accountIcon} />
                          </IconButton>
                        )}
                      </Popover>
                      <Popover
                        tooltipOpen={helpTooltipOpen}
                        onClose={handleHelpTooltipClose}
                        content={
                          <div>
                            <div>Need support? Call CAMS</div>
                            <Typography
                              component={"a"}
                              href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                              sx={{
                                color: "#08467F",
                                fontWeight: 700,
                                fontSize: "14px",
                                "&:hover": {
                                  cursor: "pointer",
                                },
                              }}
                            >
                              1-800-500-1973
                            </Typography>
                          </div>
                        }
                      >
                        <IconButton
                          onClick={toggleHelpTooltip}
                          data-testid="help-btn"
                        >
                          <img src={helpIcon} />
                        </IconButton>
                      </Popover>
                    </Box>
                  </ClickAwayListener>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Menu
          firstName={vipAccount?.firstName}
          lastName={vipAccount?.lastName}
          isOpen={isOpen}
        />
      </Box>
    )
  );
};

export default Header;
